import React, { useRef } from "react";
import { sanitizeSvg, nextSvgUid } from "../SvgDrawing/SvgDrawing";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Divider,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { TurnInfo, TurnMode, Player, Cue } from "../../shared/interfaces/GameLogicTypes";
import { renderCueContent } from "../../shared/helper/renderCueContent";

interface GameReviewProps {
  open: boolean;
  onClose: () => void;
  turnHistory: TurnInfo[];
  players: { [playerId: string]: Player };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function stripHtml(s: string): string {
  return s.replace(/<h>/g, "").replace(/<\/h>/g, "");
}

function formatAboutTsar(template: string, tsarName: string): string {
  return stripHtml(template).replace(/\{\{tsar\}\}/g, tsarName);
}

function formatSequence(sequence: number[], cues: Cue[]): string {
  return sequence.map((pos) => cues[pos - 1]?.content ?? "?").join(" > ");
}

function optionText(choice: number | null | undefined, cues: Cue[]): string {
  if (choice == null || choice === 0) return "—";
  return cues[choice - 1]?.content ?? "?";
}

function SurvivalChip({ survived, wasAlive }: { survived: boolean; wasAlive: boolean }) {
  return (
    <Chip
      label={survived ? "survived" : wasAlive ? "eliminated" : "dead"}
      size="small"
      color={survived ? "success" : wasAlive ? "error" : "default"}
      sx={{ height: 16, fontSize: 9 }}
    />
  );
}

// ── Grouping ──────────────────────────────────────────────────────────────────

type ReviewItem =
  | { type: "standard"; turn: TurnInfo; idx: number }
  | { type: "ftlStreak"; streakId: string; turns: { turn: TurnInfo; idx: number }[] }
  | { type: "herdStreak"; streakId: string; turns: { turn: TurnInfo; idx: number }[] }
  | { type: "rr"; turn: TurnInfo; idx: number }
  | { type: "picasso"; turn: TurnInfo; idx: number };

function groupTurns(turnHistory: TurnInfo[]): ReviewItem[] {
  const items: ReviewItem[] = [];
  for (let i = 0; i < turnHistory.length; i++) {
    const turn = turnHistory[i];
    const mode = turn.turnMode;

    if (mode === TurnMode.FOLLOW_THE_LEADER && turn.ftlData) {
      const last = items[items.length - 1];
      if (last && last.type === "ftlStreak" && last.streakId === turn.ftlData.streakId) {
        last.turns.push({ turn, idx: i });
      } else {
        items.push({ type: "ftlStreak", streakId: turn.ftlData.streakId, turns: [{ turn, idx: i }] });
      }
    } else if (mode === TurnMode.RUN_WITH_THE_HERD && turn.herdData) {
      const last = items[items.length - 1];
      if (last && last.type === "herdStreak" && last.streakId === turn.herdData.streakId) {
        last.turns.push({ turn, idx: i });
      } else {
        items.push({ type: "herdStreak", streakId: turn.herdData.streakId, turns: [{ turn, idx: i }] });
      }
    } else if (mode === TurnMode.REVERSE_ROUND) {
      items.push({ type: "rr", turn, idx: i });
    } else if (mode === TurnMode.PICASSO) {
      items.push({ type: "picasso", turn, idx: i });
    } else {
      items.push({ type: "standard", turn, idx: i });
    }
  }
  return items;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function HighlightedName({ text, name }: { text: string; name: string }) {
  if (!name || !text.includes(name)) return <>{text}</>;
  const parts = text.split(name);
  return (
    <>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {i < parts.length - 1 && (
            <Box component="span" sx={{ color: "primary.main", fontWeight: "bold" }}>{name}</Box>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

function CueChips({ cues, isMobile }: { cues: Cue[]; isMobile: boolean }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
      {cues.map((cue, i) => (
        <Chip key={i} label={<>{i + 1}. {renderCueContent(String(cue.content))}</>} size="small" variant="outlined"
          sx={{ fontSize: isMobile ? 10 : undefined, height: isMobile ? 20 : undefined }} />
      ))}
    </Stack>
  );
}

// ── Standard Turn ─────────────────────────────────────────────────────────────

function StandardTurnAccordion({ turn, idx, players, isMobile }: {
  turn: TurnInfo; idx: number; players: { [id: string]: Player }; isMobile: boolean;
}) {
  const tsarName = players[turn.tsarId]?.name ?? turn.results[turn.tsarId]?.playerInfo?.name ?? "Unknown";
  const aboutTsarFormatted = formatAboutTsar(turn.question?.aboutTsar ?? turn.question?.forTsar ?? "", tsarName);
  const nonTsarResults = Object.entries(turn.results).filter(([pid]) => pid !== turn.tsarId);

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
          <Chip label={`Turn ${idx + 1}`} size="small" color="primary" sx={{ flexShrink: 0, mt: "2px" }} />
          {turn.skipped && <Chip label="skipped" size="small" color="warning" sx={{ flexShrink: 0, mt: "2px" }} />}
          <Typography variant="body2" sx={{ flex: 1 }}>
            <HighlightedName text={aboutTsarFormatted} name={tsarName} />
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ fontStyle: "italic", mb: 1, fontSize: isMobile ? 11 : undefined }}>
          <HighlightedName text={aboutTsarFormatted} name={tsarName} />
        </Typography>
        <CueChips cues={turn.cues} isMobile={isMobile} />
        <Divider sx={{ mb: 1 }} />
        <Stack spacing={isMobile ? 1 : 0.5}>
          <Stack direction={isMobile ? "column" : "row"} spacing={0.5} alignItems={isMobile ? "flex-start" : "center"}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main", fontSize: isMobile ? 11 : undefined }}>
              {tsarName} (Tsar)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined, wordBreak: "break-word" }}>
              {turn.submissions[turn.tsarId] ? formatSequence(turn.submissions[turn.tsarId], turn.cues) : "No submission"}
            </Typography>
          </Stack>
          {nonTsarResults.map(([pid, result]) => {
            const name = result.playerInfo?.name ?? players[pid]?.name ?? "Unknown";
            const pts = result.playerTurnResults?.points ?? 0;
            const seq = result.playerTurnResults?.sequence ?? [];
            return (
              <Stack key={pid} direction={isMobile ? "column" : "row"} spacing={0.5} alignItems={isMobile ? "flex-start" : "center"}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: isMobile ? 11 : undefined }}>{name}</Typography>
                  <Chip label={`${pts}`} size="small"
                    color={pts >= 1800 ? "success" : pts >= 1000 ? "default" : "error"} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined, wordBreak: "break-word" }}>
                  {seq.length > 0 ? formatSequence(seq, turn.cues) : "No submission"}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ── FTL Streak Block ──────────────────────────────────────────────────────────

function FtlStreakAccordion({ streakTurns, players, isMobile }: {
  streakTurns: { turn: TurnInfo; idx: number }[]; players: { [id: string]: Player }; isMobile: boolean;
}) {
  const firstTurn = streakTurns[0].turn;
  const czarName = players[firstTurn.tsarId]?.name ?? "Unknown";
  const firstIdx = streakTurns[0].idx;

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Turn ${firstIdx + 1}–${streakTurns[streakTurns.length - 1].idx + 1}`} size="small" color="primary" sx={{ flexShrink: 0 }} />
          <Chip label="FTL" size="small" sx={{ bgcolor: "#ff9800", color: "#fff", flexShrink: 0 }} />
          <Typography variant="body2">{czarName} leads · {streakTurns.length} round{streakTurns.length !== 1 ? "s" : ""}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          {streakTurns.map(({ turn, idx }) => {
            const ftl = turn.ftlData!;
            const questionText = stripHtml(turn.question?.forTsar ?? "");
            const chosen = optionText(ftl.czarChoice, turn.cues);
            const allPids = Object.keys(ftl.submissions);

            return (
              <Box key={idx} sx={{ pl: 1, borderLeft: "3px solid #ff9800" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">Round {ftl.streakIndex + 1}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{questionText}</Typography>
                </Stack>
                <CueChips cues={turn.cues} isMobile={isMobile} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  Leader chose: <b>{chosen}</b> · ×{ftl.multiplier}
                </Typography>
                <Stack spacing={0.25}>
                  {allPids.filter(pid => pid !== turn.tsarId).map((pid) => {
                    const sub = ftl.submissions[pid];
                    const name = players[pid]?.name ?? "Unknown";
                    return (
                      <Stack key={pid} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" sx={{ fontWeight: "bold", minWidth: isMobile ? 60 : 90, fontSize: isMobile ? 10 : undefined }}>
                          {name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined }}>
                          {optionText(sub?.choice, turn.cues)}
                        </Typography>
                        <SurvivalChip survived={sub?.survived ?? false} wasAlive={sub?.wasAlive ?? false} />
                        {(sub?.points ?? 0) > 0 && (
                          <Chip label={`+${sub.points}`} size="small" color="success" sx={{ height: 16, fontSize: 9 }} />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Herd Streak Block ─────────────────────────────────────────────────────────

function HerdStreakAccordion({ streakTurns, players, isMobile }: {
  streakTurns: { turn: TurnInfo; idx: number }[]; players: { [id: string]: Player }; isMobile: boolean;
}) {
  const firstIdx = streakTurns[0].idx;

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Turn ${firstIdx + 1}–${streakTurns[streakTurns.length - 1].idx + 1}`} size="small" color="primary" sx={{ flexShrink: 0 }} />
          <Chip label="Herd" size="small" sx={{ bgcolor: "#4caf50", color: "#fff", flexShrink: 0 }} />
          <Typography variant="body2">Run with the Herd · {streakTurns.length} round{streakTurns.length !== 1 ? "s" : ""}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1.5}>
          {streakTurns.map(({ turn, idx }) => {
            const herd = turn.herdData!;
            const questionText = stripHtml(turn.question?.forTsar ?? "");
            const allPids = Object.keys(herd.submissions);

            return (
              <Box key={idx} sx={{ pl: 1, borderLeft: "3px solid #4caf50" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold">Round {herd.streakIndex + 1}</Typography>
                  {herd.wasSplit && <Chip label="Split!" size="small" color="warning" sx={{ height: 16, fontSize: 9 }} />}
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{questionText}</Typography>
                </Stack>
                {/* Options with vote counts */}
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.75, flexWrap: "wrap", gap: 0.5 }}>
                  {turn.cues.map((cue, i) => (
                    <Chip key={i} label={<>{renderCueContent(String(cue.content))} ({herd.optionCounts[i] ?? 0})</>} size="small" variant="outlined"
                      sx={{ fontSize: isMobile ? 10 : undefined, height: isMobile ? 20 : undefined }} />
                  ))}
                </Stack>
                <Stack spacing={0.25}>
                  {allPids.map((pid) => {
                    const sub = herd.submissions[pid];
                    const name = players[pid]?.name ?? "Unknown";
                    return (
                      <Stack key={pid} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" sx={{ fontWeight: "bold", minWidth: isMobile ? 60 : 90, fontSize: isMobile ? 10 : undefined }}>
                          {name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined }}>
                          {optionText(sub?.choice, turn.cues)}
                        </Typography>
                        <SurvivalChip survived={sub?.survived ?? false} wasAlive={sub?.wasAlive ?? false} />
                        {(sub?.points ?? 0) > 0 && (
                          <Chip label={`+${sub.points}`} size="small" color="success" sx={{ height: 16, fontSize: 9 }} />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Reverse Round ─────────────────────────────────────────────────────────────

function RrAccordion({ turn, idx, isMobile }: { turn: TurnInfo; idx: number; isMobile: boolean }) {
  const rr = turn.rrData!;
  const questionText = stripHtml(turn.question?.forTsar ?? "");

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
          <Chip label={`Turn ${idx + 1}`} size="small" color="primary" sx={{ flexShrink: 0, mt: "2px" }} />
          <Chip label="RR" size="small" sx={{ bgcolor: "#9c27b0", color: "#fff", flexShrink: 0, mt: "2px" }} />
          <Typography variant="body2" sx={{ flex: 1 }}>{questionText}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ fontStyle: "italic", mb: 1, fontSize: isMobile ? 11 : undefined }}>{questionText}</Typography>
        <CueChips cues={turn.cues} isMobile={isMobile} />
        <Divider sx={{ mb: 1 }} />
        <Stack spacing={1.5}>
          {rr.sequences.map((seqCard, si) => (
            <Box key={si}>
              <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: isMobile ? 11 : undefined, mb: 0.5 }}>
                {seqCard.isRandom ? "Random sequence" : `${seqCard.submitterName}'s ranking`}:{" "}
                <Box component="span" sx={{ fontWeight: "normal", color: "text.secondary" }}>
                  {formatSequence(seqCard.sequence, turn.cues)}
                </Box>
              </Typography>
              <Stack spacing={0.25} sx={{ pl: 1 }}>
                {seqCard.guesses.map((g, gi) => (
                  <Stack key={gi} direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: "bold", minWidth: isMobile ? 60 : 80, fontSize: isMobile ? 10 : undefined }}>
                      {g.playerName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined }}>
                      → {g.guessedName ?? "—"}
                    </Typography>
                    <Chip label={g.correct ? "✓" : "✗"} size="small" color={g.correct ? "success" : "error"}
                      sx={{ height: 16, fontSize: 9, minWidth: 24 }} />
                    {g.points > 0 && <Chip label={`+${g.points}`} size="small" color="default" sx={{ height: 16, fontSize: 9 }} />}
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Picasso ───────────────────────────────────────────────────────────────────

function PicassoAccordion({ turn, idx, isMobile }: { turn: TurnInfo; idx: number; isMobile: boolean }) {
  const pic = turn.picassoData!;
  const questionText = stripHtml(turn.question?.forTsar ?? "");
  const thumbSize = isMobile ? 50 : 60;

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
          <Chip label={`Turn ${idx + 1}`} size="small" color="primary" sx={{ flexShrink: 0, mt: "2px" }} />
          <Chip label="Picasso" size="small" sx={{ bgcolor: "#e91e63", color: "#fff", flexShrink: 0, mt: "2px" }} />
          <Typography variant="body2" sx={{ flex: 1 }}>{questionText}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ fontStyle: "italic", mb: 1, fontSize: isMobile ? 11 : undefined }}>{questionText}</Typography>
        <Divider sx={{ mb: 1 }} />
        {/* Drawings with thumbnails */}
        <Stack spacing={isMobile ? 1 : 0.5}>
          {pic.drawings.map((d) => (
            <Stack key={d.playerId} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Box
                sx={{ width: thumbSize, height: thumbSize, borderRadius: 1, overflow: "hidden", bgcolor: "#fff", flexShrink: 0, "& svg": { width: thumbSize, height: thumbSize } }}
                dangerouslySetInnerHTML={{ __html: sanitizeSvg(d.drawingSvg, `gr-${d.playerId}`) }}
              />
              <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: isMobile ? 60 : 80, fontSize: isMobile ? 11 : undefined }}>
                {d.playerName}
              </Typography>
              {d.isImposter && <Chip label="Imposter" size="small" color="warning" sx={{ height: 18, fontSize: 9 }} />}
              {d.correctGuessCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined }}>
                  {d.correctGuessCount} recognized
                </Typography>
              )}
              {d.pointsEarned > 0 && <Chip label={`+${d.pointsEarned}`} size="small" color="success" sx={{ height: 18, fontSize: 9 }} />}
              {d.pointBreakdown && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 9 : 10 }}>
                  ({d.pointBreakdown})
                </Typography>
              )}
              {d.isImposter && d.imposterTarget && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? 10 : undefined }}>
                  (mimicked {d.imposterTarget})
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
        {/* Challenge results */}
        {pic.challengeResults && pic.challengeResults.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>Challenges</Typography>
            <Stack spacing={0.5}>
              {pic.challengeResults.map((c, ci) => (
                <Box key={ci} sx={{ pl: 0.5 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={c.type}
                      size="small"
                      sx={{
                        height: 16, fontSize: 9,
                        bgcolor: c.type === "imposter" ? "#d32f2f" : c.type === "archive" ? "#0288d1" : "#616161",
                        color: "#fff",
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: isMobile ? 10 : undefined }}>
                      {c.targetPlayerName}: {c.drawerAName} vs {c.drawerBName}
                    </Typography>
                  </Stack>
                  {c.voters.map((v, vi) => (
                    <Typography key={vi} variant="caption" sx={{ pl: 1, display: "block", color: v.correct ? "#66bb6a" : "#ef5350", fontSize: isMobile ? 9 : 10 }}>
                      {v.playerName}: {v.vote} {v.correct ? "\u2713" : "\u2717"} {v.points > 0 ? `+${v.points}` : ""}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {/* Imposter results */}
        {pic.imposterResults.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 1 }} />
            {pic.imposterResults.map((imp, ii) => (
              <Typography key={ii} variant="caption" color="text.secondary" sx={{ display: "block", fontSize: isMobile ? 10 : undefined }}>
                {imp.imposterName} → {imp.targetName}:{" "}
                {imp.accepted ? (imp.fooledCount > 0 ? `fooled ${imp.fooledCount}` : "fooled nobody") : "declined"} (+{imp.imposterPoints})
              </Typography>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function GameReview({ open, onClose, turnHistory, players }: GameReviewProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const items = groupTurns(turnHistory);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth={!isMobile}
      PaperProps={{ sx: { height: isMobile ? "100%" : "80vh" } }}
    >
      <DialogTitle>
        <Typography variant="h6">Full Game Review</Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1, overflow: "auto" }}>
        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
            No turns recorded.
          </Typography>
        ) : (
          items.map((item, i) => {
            if (item.type === "ftlStreak") {
              return <FtlStreakAccordion key={i} streakTurns={item.turns} players={players} isMobile={isMobile} />;
            }
            if (item.type === "herdStreak") {
              return <HerdStreakAccordion key={i} streakTurns={item.turns} players={players} isMobile={isMobile} />;
            }
            if (item.type === "rr") {
              return <RrAccordion key={i} turn={item.turn} idx={item.idx} isMobile={isMobile} />;
            }
            if (item.type === "picasso") {
              return <PicassoAccordion key={i} turn={item.turn} idx={item.idx} isMobile={isMobile} />;
            }
            return <StandardTurnAccordion key={i} turn={item.turn} idx={item.idx} players={players} isMobile={isMobile} />;
          })
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
